import { apiClient } from "./client";

export interface AlertConfig {
  id: number;
  key: string;
  enabled: boolean;
  day_of_month: number;
  hour: number;
  recipients: string[];
  last_run_month: string | null;
  last_run_at: string | null;
  last_status: string | null;
  updated_at: string;
}

export interface AlertConfigUpdate {
  enabled?: boolean;
  day_of_month?: number;
  hour?: number;
  recipients?: string[];
}

export interface HorsPolitiqueConfigUpdate {
  enabled?: boolean;
  recipients?: string[];
}

export interface AlertRun {
  id: number;
  config_key: string;
  run_at: string;
  triggered_by: string;
  recipients_count: number;
  success_count: number;
  error_count: number;
  skipped_count: number;
  status: string;
  detail: {
    period_label?: string;
    simulation_id?: number;
    simulation_ref?: string | null;
    client?: string;
    simulator?: string;
    marge?: number | null;
    marge_min?: number | null;
    marge_max?: number | null;
    offre?: string | null;
    results?: Array<{
      email: string;
      status: string;
      detail: string | null;
      metrics?: {
        simulations: number;
        cotations: number;
        proposals: number;
        hors_politique: number;
      } | null;
    }>;
  } | null;
}

export interface PncStatus {
  apim_env: string;
  apim_credentials: boolean;
  partner: boolean;
  model_code: boolean;
  model_version: number;
  body_variable: boolean;
  ready: boolean;
  apim_healthy: boolean;
  responsables_count: number;
}

export interface MonthlySummaryPreview {
  user: { id: number; email: string; full_name: string | null };
  subject: string;
  html: string;
}

export interface RunNowResult {
  period_label: string;
  triggered_by: string;
  recipients_count: number;
  success_count: number;
  error_count: number;
  skipped_count: number;
  status: string;
}

export interface TestSendResult {
  email: string;
  subject: string;
  status: string;
  success: boolean;
  detail: string | null;
  notification_uuid: string | null;
}

export async function getMonthlySummaryConfig(): Promise<AlertConfig> {
  const response = await apiClient.get<AlertConfig>("/alerts/monthly-summary");
  return response.data;
}

export async function updateMonthlySummaryConfig(
  data: AlertConfigUpdate
): Promise<AlertConfig> {
  const response = await apiClient.put<AlertConfig>(
    "/alerts/monthly-summary",
    data
  );
  return response.data;
}

export async function getMonthlySummaryPreview(
  userId?: number
): Promise<MonthlySummaryPreview> {
  const params: Record<string, unknown> = {};
  if (userId !== undefined) params.user_id = userId;
  const response = await apiClient.get<MonthlySummaryPreview>(
    "/alerts/monthly-summary/preview",
    { params }
  );
  return response.data;
}

export async function testSendMonthlySummary(
  email: string,
  userId?: number,
  bodyVariable?: string
): Promise<TestSendResult> {
  const response = await apiClient.post<TestSendResult>(
    "/alerts/monthly-summary/test",
    { email, user_id: userId, body_variable: bodyVariable || undefined }
  );
  return response.data;
}

export async function runMonthlySummaryNow(): Promise<RunNowResult> {
  const response = await apiClient.post<RunNowResult>(
    "/alerts/monthly-summary/run-now"
  );
  return response.data;
}

export async function listMonthlySummaryRuns(
  limit = 20
): Promise<AlertRun[]> {
  const response = await apiClient.get<AlertRun[]>(
    "/alerts/monthly-summary/runs",
    { params: { limit } }
  );
  return response.data;
}

export async function getPncStatus(): Promise<PncStatus> {
  const response = await apiClient.get<PncStatus>("/alerts/pnc-status");
  return response.data;
}

// --- Alerte "cotation hors politique tarifaire" ---

export interface HorsPolitiquePreview {
  subject: string;
  html: string;
}

export async function getHorsPolitiqueConfig(): Promise<AlertConfig> {
  const response = await apiClient.get<AlertConfig>("/alerts/hors-politique");
  return response.data;
}

export async function updateHorsPolitiqueConfig(
  data: HorsPolitiqueConfigUpdate
): Promise<AlertConfig> {
  const response = await apiClient.put<AlertConfig>(
    "/alerts/hors-politique",
    data
  );
  return response.data;
}

export async function getHorsPolitiquePreview(): Promise<HorsPolitiquePreview> {
  const response = await apiClient.get<HorsPolitiquePreview>(
    "/alerts/hors-politique/preview"
  );
  return response.data;
}

export async function testSendHorsPolitique(
  email: string
): Promise<TestSendResult> {
  const response = await apiClient.post<TestSendResult>(
    "/alerts/hors-politique/test",
    { email }
  );
  return response.data;
}

export async function listHorsPolitiqueRuns(limit = 20): Promise<AlertRun[]> {
  const response = await apiClient.get<AlertRun[]>(
    "/alerts/hors-politique/runs",
    { params: { limit } }
  );
  return response.data;
}
